import React from 'react';
import { useMutation } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { Field } from '../../components/Form';
import FormModal from '../../components/FormModal';
import { gql } from '../../graphql/generated/gql';
import { adminToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import Link from '../../utils/router/Link';
import * as styles from './EnableSAMLIntegrationModal.module.scss';

const CREATE_SAML_INTEGRATION_MUTATION = gql(/** @complexity 2 */ `
  mutation createSamlIntegration($platform: String!, $idpMetadata: String!) {
    createSamlIntegration(platform: $platform, idpMetadata: $idpMetadata) {
      id
      platform
    }
  }
`);

const PLATFORMS: Record<
  string,
  { name: string; description: React.ReactNode }
> = {
  okta: {
    name: 'Okta',
    description: (
      <>
        Follow our{' '}
        <Link to="https://go.slab.com/saml-2-0-sso-with-okta" newTab>
          guide on setting up the Slab SAML app
        </Link>{' '}
        in your Okta admin dashboard using your unique Org ID above. Once
        correctly set up, copy your IdP Metadata and paste it below:
      </>
    ),
  },
  onelogin: {
    name: 'OneLogin',
    description: (
      <>
        Follow our{' '}
        <Link to="https://go.slab.com/saml-2-0-sso-with-onelogin" newTab>
          guide on setting up the Slab SAML app
        </Link>{' '}
        in your OneLogin admin dashboard using your unique Org ID above. Once
        correctly set up, you&apos;ll be able to download your IdP Metadata.
        Paste the contents of that file below:
      </>
    ),
  },
};

export const isSAMLIntegration = (platform: string) => !!PLATFORMS[platform];

interface EnableSAMLIntegrationModalProps {
  platform: string;
  close: () => void;
}

const EnableSAMLIntegrationModal = ({
  platform,
  close,
}: EnableSAMLIntegrationModalProps) => {
  const orgId = useAppSelector((state) => state.session.organization.id);
  const dispatch = useAppDispatch();
  const [createIntegration] = useMutation(CREATE_SAML_INTEGRATION_MUTATION);

  const platformInfo = PLATFORMS[platform];
  if (!platformInfo) {
    return null;
  }
  const { name, description } = platformInfo;

  const handleSubmit = (params: { idpMetadata: string }) => {
    return createIntegration({ variables: { platform, ...params } })
      .then(() => document.location.reload())
      .catch(() =>
        dispatch(
          UIActions.pushToast(
            adminToasts.samlIntegrationError({ platform: name }),
          ),
        ),
      );
  };

  return (
    <FormModal<{ idpMetadata: string }>
      variant="expanded"
      title={`Enable ${name}`}
      close={close}
      width={500}
      form={{
        initialValues: { idpMetadata: '' },
        onSubmit: handleSubmit,
      }}
      footer={{
        submitButton: { text: 'Enable Integration' },
      }}
    >
      <div className={styles.title}>Your Org ID</div>
      <div className={styles.content}>
        Your team&apos;s unique Org ID is: <strong>{orgId}</strong>
      </div>

      <div className={styles.title}>Get your SAML Metadata</div>
      <div className={styles.content}>{description}</div>

      <Field
        className={styles.idpMetadata}
        name="idpMetadata"
        type="textarea"
        label="IDP Metadata"
        required
      />
    </FormModal>
  );
};

export default EnableSAMLIntegrationModal;
